import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useLookups } from '../../lib/lookups';
import { money } from '../../lib/hooks';
import { Roles, useAuth } from '../../app/providers';
import { api } from '../../lib/api';
import { useMutation } from '../../lib/hooks';

type Student = {
  id: number;
  name: string;
  fatherName: string;
  schoolName?: string;
  studentMobileNo?: string;
  parentMobileNo?: string;
  isActive: boolean;
  grade?: { description: string };
};

type Teacher = {
  id: number;
  name: string;
  email: string;
  mobileNo: string;
  association?: string;
  qualification?: string;
  isActive: boolean;
};

type Staff = {
  id: number;
  name: string;
  email?: string;
  mobileNo?: string;
  isActive: boolean;
  designation?: { description: string };
  salary?: { amount: number };
};

type Course = {
  id: number;
  groupName: string;
  sessionStartYear: number;
  sessionEndYear: number;
  isActive: boolean;
  grade?: { description: string };
  subject?: { description: string };
  teacher?: { name: string };
  courseFees?: { amount: number };
  courseSchedules?: { day: string; startTime: string }[];
};

/** Shared active/suspended pill. */
function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span className={isActive ? 'badge badge--paid' : 'badge badge--due'}>
      {isActive ? 'Active' : 'Suspended'}
    </span>
  );
}

/**
 * Suspend/reactivate toggle.
 *
 * Not a delete, so it does not go through the confirmation dialog - but it does toast,
 * and it reloads the list so the badge reflects what the server actually decided.
 */
function ToggleStatusButton({
  endpoint,
  id,
  isActive,
  reload,
}: {
  endpoint: string;
  id: number;
  isActive: boolean;
  reload: () => Promise<void>;
}) {
  const { run, busy } = useMutation();

  return (
    <button
      type="button"
      className="btn btn--sm"
      disabled={busy}
      onClick={() =>
        void run(
          () => api.post(`${endpoint}/${id}/toggle-status`),
          isActive ? 'Suspended.' : 'Reactivated.',
          reload,
        )
      }
    >
      {isActive ? 'Suspend' : 'Activate'}
    </button>
  );
}

export function StudentsPage() {
  const navigate = useNavigate();
  const lookups = useLookups();

  return (
    <ListPage<Student>
      filters={[
        { key: 'id', label: 'Student ID', type: 'number', placeholder: 'e.g. 42' },
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'fatherName', label: 'Father', type: 'text' },
        { key: 'gradeId', label: 'Grade', type: 'select', options: lookups.grades },
        { key: 'schoolName', label: 'School', type: 'text' },
        { key: 'mobile', label: 'Mobile', type: 'text', placeholder: '92-300…' },
        // Opens on Active. Suspended records are still there - switch this to Any to see
        // everything, or to Suspended to see only those.
        { key: 'isActive', label: 'Status', type: 'select', options: lookups.active, defaultValue: 'true' },
      ]}
      actions={
        <button type="button" className="btn btn--accent" onClick={() => navigate('/students/new')}>
          New student
        </button>
      }
      title="Students"
      subtitle="Registered students. Opens on Active — switch Status to Any or Suspended to see the rest. Suspend is reversible; Remove takes the student off this list for good."
      endpoint="/api/students"
      rowKey={(r) => r.id}
      emptyMessage="No students found."
      columns={[
        { key: 'id', header: 'ID', render: (r) => `STD-${r.id}`, width: '90px', nowrap: true },
        { key: 'name', header: 'Name', render: (r) => r.name },
        { key: 'father', header: 'Father', render: (r) => r.fatherName },
        { key: 'grade', header: 'Grade', render: (r) => r.grade?.description ?? '—' },
        { key: 'mobile', header: 'Mobile', render: (r) => r.studentMobileNo ?? '—' },
        { key: 'status', header: 'Status', render: (r) => <StatusBadge isActive={r.isActive} /> },
      ]}
      rowActions={(row, reload) => (
        <>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/students/' + row.id + '/edit')}>Edit</button>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/students/' + row.id + '/enroll')}>Courses</button>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/fees/change/' + row.id)}>Fees</button>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/students/' + row.id + '/tests')}>Tests</button>
          <ToggleStatusButton endpoint="/api/students" id={row.id} isActive={row.isActive} reload={reload} />
        </>
      )}
      remove={{
        url: (r) => `/api/students/${r.id}`,
        label: 'Remove',
        successMessage: 'Student removed.',
        describe: (r) => (
          <>
            <p>
              Remove <strong>{r.name}</strong> (STD-{r.id})?
            </p>
            <p>
              They will be un-enrolled from all courses and will <strong>no longer appear on
              this list</strong>, under any Status filter. Their fee history is kept, and
              nothing is erased from the database.
            </p>
            <p>
              To pause a student temporarily, use <strong>Suspend</strong> instead — that is
              reversible from this screen.
            </p>
          </>
        ),
      }}
    />
  );
}

export function TeachersPage() {
  const { hasRole } = useAuth();
  const { run, busy } = useMutation();
  const navigate = useNavigate();
  const lookups = useLookups();

  return (
    <ListPage<Teacher>
      filters={[
        // One filter per visible column (Staff ID mirrors Teacher ID, so it shares the ID box).
        { key: 'id', label: 'Teacher ID', type: 'number' },
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'mobileNo', label: 'Mobile', type: 'text' },
        { key: 'email', label: 'Email', type: 'text' },
        { key: 'association', label: 'Association', type: 'text' },
        { key: 'qualification', label: 'Qualification', type: 'text' },
        // Opens on Active. Suspended records are still there - switch this to Any to see
        // everything, or to Suspended to see only those.
        { key: 'isActive', label: 'Status', type: 'select', options: lookups.active, defaultValue: 'true' },
      ]}
      title="Teachers"
      subtitle="Teaching staff."
      endpoint="/api/teachers"
      rowKey={(r) => r.id}
      emptyMessage="No teachers found."
      actions={
        <>
        <button type="button" className="btn btn--accent" onClick={() => navigate('/teachers/new')}>
          New teacher
        </button>
        {hasRole(Roles.Director) && (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() =>
              void run(
                () => api.post<{ created: number }>('/api/teachers/generate-accounts'),
                'Login accounts generated.',
              )
            }
          >
            Generate logins
          </button>
        )}
        </>
      }
      columns={[
        { key: 'id', header: 'Teacher ID', render: (r) => `T-${r.id}`, width: '90px', nowrap: true },
        // The legacy list prints the same prefixed id under "Staff ID"; kept for parity.
        { key: 'staffId', header: 'Staff ID', render: (r) => `T-${r.id}`, width: '90px', nowrap: true },
        { key: 'name', header: 'Name', render: (r) => r.name },
        { key: 'mobile', header: 'Mobile', render: (r) => r.mobileNo },
        { key: 'email', header: 'Email', render: (r) => r.email },
        // Tolerant of '' and whitespace, not just null: rows created by the early React form
        // stored an empty string where the legacy form stored NULL.
        { key: 'association', header: 'Association', render: (r) => (r.association?.trim() ? r.association : '—') },
        { key: 'qualification', header: 'Qualification', render: (r) => (r.qualification?.trim() ? r.qualification : '—') },
        { key: 'status', header: 'Status', render: (r) => <StatusBadge isActive={r.isActive} /> },
      ]}
      rowActions={(row, reload) => (
        <>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/teachers/' + row.id + '/edit')}>Edit</button>
          <ToggleStatusButton endpoint="/api/teachers" id={row.id} isActive={row.isActive} reload={reload} />
        </>
      )}
      remove={{
        url: (r) => `/api/teachers/${r.id}`,
        label: 'Remove',
        successMessage: 'Teacher removed.',
        describe: (r) => (
          <>
            <p>
              Remove <strong>{r.name}</strong> (T-{r.id})?
            </p>
            <p>Their salary history and course assignments are kept.</p>
          </>
        ),
      }}
    />
  );
}

export function StaffPage() {
  const navigate = useNavigate();
  const lookups = useLookups();

  return (
    <ListPage<Staff>
      filters={[
        { key: 'id', label: 'Staff ID', type: 'number' },
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'designationId', label: 'Designation', type: 'select', options: lookups.designations },
        { key: 'email', label: 'Email', type: 'text' },
        { key: 'mobileNo', label: 'Mobile', type: 'text' },
        // Opens on Active. Suspended records are still there - switch this to Any to see
        // everything, or to Suspended to see only those.
        { key: 'isActive', label: 'Status', type: 'select', options: lookups.active, defaultValue: 'true' },
      ]}
      actions={
        <button type="button" className="btn btn--accent" onClick={() => navigate('/staff/new')}>
          New staff member
        </button>
      }
      title="Staff"
      subtitle="Non-teaching staff."
      endpoint="/api/staff"
      rowKey={(r) => r.id}
      emptyMessage="No staff found."
      columns={[
        { key: 'id', header: 'Staff ID', render: (r) => `STF-${r.id}`, width: '90px', nowrap: true },
        { key: 'name', header: 'Name', render: (r) => r.name },
        { key: 'designation', header: 'Designation', render: (r) => r.designation?.description ?? '—' },
        { key: 'email', header: 'Email', render: (r) => r.email ?? '—' },
        { key: 'mobile', header: 'Mobile', render: (r) => r.mobileNo ?? '—' },
        { key: 'status', header: 'Status', render: (r) => <StatusBadge isActive={r.isActive} /> },
      ]}
      rowActions={(row, reload) => (
        <>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/staff/' + row.id + '/edit')}>Edit</button>
          <ToggleStatusButton endpoint="/api/staff" id={row.id} isActive={row.isActive} reload={reload} />
        </>
      )}
      remove={{
        url: (r) => `/api/staff/${r.id}`,
        label: 'Remove',
        successMessage: 'Staff member removed.',
        describe: (r) => (
          <p>
            Remove <strong>{r.name}</strong> (STF-{r.id})? Their salary history is kept.
          </p>
        ),
      }}
    />
  );
}

export function CoursesPage() {
  const navigate = useNavigate();
  const lookups = useLookups();

  return (
    <ListPage<Course>
      filters={[
        { key: 'id', label: 'Course ID', type: 'number' },
        { key: 'gradeId', label: 'Grade', type: 'select', options: lookups.grades },
        { key: 'subjectId', label: 'Subject', type: 'select', options: lookups.subjects },
        { key: 'teacherId', label: 'Teacher', type: 'select', options: lookups.teachers },
        { key: 'groupName', label: 'Group', type: 'text' },
        { key: 'sessionStartYear', label: 'Session start', type: 'number', placeholder: 'e.g. 2026' },
        // Opens on Active. Suspended records are still there - switch this to Any to see
        // everything, or to Suspended to see only those.
        { key: 'isActive', label: 'Status', type: 'select', options: lookups.active, defaultValue: 'true' },
      ]}
      actions={
        <button type="button" className="btn btn--accent" onClick={() => navigate('/courses/new')}>
          New course
        </button>
      }
      title="Courses"
      subtitle="Grades, subjects and the teacher assigned to each group."
      endpoint="/api/courses"
      rowKey={(r) => r.id}
      emptyMessage="No courses found."
      columns={[
        { key: 'id', header: 'ID', render: (r) => `C-${r.id}`, width: '80px', nowrap: true },
        { key: 'grade', header: 'Grade', render: (r) => r.grade?.description ?? '—' },
        { key: 'subject', header: 'Subject', render: (r) => r.subject?.description ?? '—' },
        { key: 'group', header: 'Group', render: (r) => r.groupName },
        { key: 'teacher', header: 'Teacher', render: (r) => r.teacher?.name ?? '—' },
        {
          key: 'session',
          header: 'Session',
          render: (r) => `${r.sessionStartYear}–${r.sessionEndYear}`,
        },
        {
          // Matches the legacy list: "Day (StartTime)" per slot, comma-joined, or "None".
          key: 'days',
          header: 'Days (Start Time)',
          render: (r) => {
            const slots = r.courseSchedules ?? [];
            return slots.length
              ? slots.map((s) => `${s.day} (${s.startTime})`).join(', ')
              : 'None';
          },
        },
        {
          key: 'fees',
          header: 'Fee',
          align: 'right',
          render: (r) => <span className="money">{money(r.courseFees?.amount)}</span>,
        },
        { key: 'status', header: 'Status', render: (r) => <StatusBadge isActive={r.isActive} /> },
      ]}
      rowActions={(row, reload) => (
        <>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/courses/' + row.id + '/edit')}>Edit</button>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/courses/' + row.id + '/schedules')}>Schedule</button>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/courses/' + row.id + '/students')}>Students</button>
          <ToggleStatusButton endpoint="/api/courses" id={row.id} isActive={row.isActive} reload={reload} />
        </>
      )}
      remove={{
        url: (r) => `/api/courses/${r.id}`,
        label: 'Remove',
        successMessage: 'Course removed.',
        describe: (r) => (
          <>
            <p>
              Remove course <strong>C-{r.id}</strong> ({r.grade?.description} · {r.subject?.description} ·{' '}
              {r.groupName})?
            </p>
            <p>Enrolments and fee history are kept.</p>
          </>
        ),
      }}
    />
  );
}
